import type { WsStatus } from '../sync/WsClient';
import { AlertIcon, RefreshIcon } from './icons';

interface Props {
  status: WsStatus;
}

interface Banner {
  text: string;
  tone: 'info' | 'warn' | 'error';
  assertive?: boolean;
}

export function describe(status: WsStatus): Banner | null {
  switch (status.t) {
    case 'open':
      return null;
    case 'connecting':
      return { text: '正在连接画板…', tone: 'info' };
    case 'reconnecting': {
      const sec = Math.max(1, Math.round(status.nextDelayMs / 1000));
      return { text: `已断开，正在重连…（${sec}s 后重试）`, tone: 'warn' };
    }
    case 'fatal': {
      const r = status.reason;
      if (r === 'invalid_token') {
        return { text: '登录已过期，正在跳转登录页…', tone: 'error', assertive: true };
      }
      if (r === 'rate_limit' || r === 'commit_rate_limit') {
        return {
          text: '操作过于频繁，连接已被服务端限流，请稍后刷新重试。（不会强制退出登录）',
          tone: 'warn',
        };
      }
      if (r === 'no_board') {
        return { text: '画板不可用，可能已被删除或归档。', tone: 'error', assertive: true };
      }
      return { text: `连接已关闭：${r || '未知原因'}`, tone: 'error', assertive: true };
    }
  }
}

// Tone → tailwind classes. No inline-style hex; banner is themed via the nju
// brand palette (info) plus semantic warn/error accents that stay legible on
// every board theme. Banner is rendered as a centered pill so it does not
// overlap the BoardSwitcher anchored at top-left.
const TONE_CLASS: Record<Banner['tone'], string> = {
  info: 'bg-brand-600 text-white',
  warn: 'bg-amber-500 text-neutral-900',
  error: 'bg-red-600 text-white',
};

export default function ConnectionBanner({ status }: Props) {
  const banner = describe(status);
  if (!banner) return null;
  const Icon = banner.tone === 'info' ? RefreshIcon : AlertIcon;
  return (
    <div
      role="status"
      aria-live={banner.assertive ? 'assertive' : 'polite'}
      className={`bb-fade-in fixed top-3 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[13px] font-medium shadow-elev1 ${TONE_CLASS[banner.tone]}`}
    >
      <Icon size={14} />
      <span>{banner.text}</span>
    </div>
  );
}
